import React, { useMemo } from "react";
import { TreeSelect } from "antd";
import { useProductTypesQuery } from "../hooks/useProductTypesQuery";
import { compareProductTypes } from "../productTypeTree";

type ProductTypeTreeSelectProps = {
  value?: number | null;
  onChange?: (value: number | null) => void;
  excludeId?: number;
  allowClear?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
};

type TreeNode = {
  title: string;
  value: number;
  key: number;
  children?: TreeNode[];
  disabled?: boolean;
};

const ProductTypeTreeSelect: React.FC<ProductTypeTreeSelectProps> = ({
  value,
  onChange,
  excludeId,
  allowClear = true,
  placeholder = "Välj produkttyp",
  style,
}) => {
  const { data: productTypes = [], isLoading } = useProductTypesQuery();

  const treeData = useMemo(() => {
    const byParent = new Map<number | null, typeof productTypes>();
    productTypes.forEach((pt) => {
      const parentKey = pt.parentProductTypeId ?? null;
      const list = byParent.get(parentKey) ?? [];
      list.push(pt);
      byParent.set(parentKey, list);
    });

    byParent.forEach((list, parentKey) => {
      byParent.set(parentKey, list.slice().sort(compareProductTypes));
    });

    const descendantIds = new Set<number>();
    if (excludeId != null) {
      const collect = (id: number) => {
        descendantIds.add(id);
        (byParent.get(id) ?? []).forEach((child) => collect(child.id));
      };
      collect(excludeId);
    }

    const build = (parentId: number | null): TreeNode[] =>
      (byParent.get(parentId) ?? []).map((pt) => ({
        title: pt.name || `Typ #${pt.id}`,
        value: pt.id,
        key: pt.id,
        disabled: descendantIds.has(pt.id),
        children: build(pt.id),
      }));

    return build(null);
  }, [productTypes, excludeId]);

  return (
    <TreeSelect
      style={{ width: "100%", ...style }}
      value={value ?? undefined}
      onChange={(next) => onChange?.(next ?? null)}
      treeData={treeData}
      loading={isLoading}
      allowClear={allowClear}
      placeholder={placeholder}
      treeDefaultExpandAll
      showSearch
      treeNodeFilterProp="title"
    />
  );
};

export default ProductTypeTreeSelect;
